import { appConfig } from "@/config";
import { baseRefreshingQuery } from "@/core/api/baseQuery";
import { HttpMethod } from "@/types";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  CreateDashboardConfiguration,
  UpdateDashboardConfiguration,
  DashboardConfiguration,
  DashboardDeployPayload,
} from "@repo/schema";

export enum DashboardConfigApiTagType {
  DashboardConfigs = "DashboardConfigs",
}

const { dashboardConfig } = appConfig.studioApi.endpoints.projects;

export const dashboardConfigApi = createApi({
  reducerPath: "dashboardConfigApi",
  baseQuery: baseRefreshingQuery,
  tagTypes: Object.values(DashboardConfigApiTagType),
  endpoints: (builder) => ({
    // --- Dashboard Configuration endpoints ---
    getDashboardConfigs: builder.query<
      DashboardConfiguration[],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: dashboardConfig.configurations(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: DashboardConfigApiTagType.DashboardConfigs, id: projectId },
      ],
    }),
    getDashboardConfig: builder.query<
      DashboardConfiguration,
      { projectId: number; configId: number }
    >({
      query: ({ projectId, configId }) => ({
        url: dashboardConfig.configuration(projectId, configId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { configId }) => [
        { type: DashboardConfigApiTagType.DashboardConfigs, id: configId },
      ],
    }),
    /**
     * Everything the camera needs to run one configuration, in a single call.
     *
     * Replaces a client-side fan-out of 5 + T + sum(L) requests per config
     * (test cases, then each test case, then each limit). The response is the
     * camera's own wire format, so the deploy hook forwards it untouched.
     *
     * `platform` picks the artifact, so it belongs to the target camera rather
     * than the configuration. A null result means this configuration has no
     * artifact that platform can run — skip it, do not fail the deploy.
     */
    getDeployPayload: builder.query<
      DashboardDeployPayload | null,
      { projectId: number; configId: number; platform: string }
    >({
      query: ({ projectId, configId, platform }) => ({
        url: dashboardConfig.deployPayload(projectId, configId),
        method: HttpMethod.GET,
        params: { platform },
      }),
    }),
    createDashboardConfig: builder.mutation<
      DashboardConfiguration,
      CreateDashboardConfiguration & { projectId: number }
    >({
      query: ({ projectId, ...body }) => ({
        url: dashboardConfig.configurations(projectId),
        method: HttpMethod.POST,
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: DashboardConfigApiTagType.DashboardConfigs, id: projectId },
      ],
    }),
    updateDashboardConfig: builder.mutation<
      DashboardConfiguration,
      UpdateDashboardConfiguration & { projectId: number; configId: number }
    >({
      query: ({ projectId, configId, ...body }) => ({
        url: dashboardConfig.configuration(projectId, configId),
        method: HttpMethod.PUT,
        body,
      }),
      invalidatesTags: (_result, _error, { projectId, configId }) => [
        { type: DashboardConfigApiTagType.DashboardConfigs, id: projectId },
        { type: DashboardConfigApiTagType.DashboardConfigs, id: configId },
      ],
    }),
    deleteDashboardConfig: builder.mutation<
      void,
      { projectId: number; configId: number }
    >({
      query: ({ projectId, configId }) => ({
        url: dashboardConfig.configuration(projectId, configId),
        method: HttpMethod.DELETE,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: DashboardConfigApiTagType.DashboardConfigs, id: projectId },
      ],
    }),
  }),
});

export const {
  useGetDashboardConfigsQuery,
  useLazyGetDashboardConfigsQuery,
  useLazyGetDeployPayloadQuery,
  useGetDashboardConfigQuery,
  useCreateDashboardConfigMutation,
  useUpdateDashboardConfigMutation,
  useDeleteDashboardConfigMutation,
} = dashboardConfigApi;
